import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AuthCallback - Handles Google OAuth redirect
 * Processes the session_id from the URL fragment and exchanges it for a session token
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Log the full URL for debugging
        console.log("[AuthCallback] Full URL:", window.location.href);
        console.log("[AuthCallback] Hash:", window.location.hash);
        console.log("[AuthCallback] Search:", window.location.search);
        
        // Extract session_id from URL fragment (not query params)
        let sessionId = null;
        
        // Try hash first (fragment)
        const hash = window.location.hash;
        const hashMatch = hash.match(/session_id=([^&]+)/);
        if (hashMatch) {
          sessionId = hashMatch[1];
          console.log("[AuthCallback] Found session_id in hash");
        }
        
        // Also check query params (some auth providers use this)
        if (!sessionId) {
          const urlParams = new URLSearchParams(window.location.search);
          sessionId = urlParams.get('session_id');
          if (sessionId) {
            console.log("[AuthCallback] Found session_id in query params");
          }
        }
        
        if (!sessionId) {
          console.error("[AuthCallback] No session_id found in URL");
          console.error("[AuthCallback] URL was:", window.location.href);
          // Wait a moment before redirecting - the hash might not be available immediately
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Try again after delay
          const retryHash = window.location.hash;
          const retryMatch = retryHash.match(/session_id=([^&]+)/);
          if (retryMatch) {
            sessionId = retryMatch[1];
            console.log("[AuthCallback] Found session_id on retry");
          } else {
            navigate("/login");
            return;
          }
        }

        console.log("[AuthCallback] Processing session_id");

        // Exchange session_id for business session
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId
        }, {
          withCredentials: true // Important for cookies
        });

        const { access_token, business } = response.data;

        // Store JWT token in localStorage (for API calls)
        localStorage.setItem("heyintake_token", access_token);
        localStorage.setItem("token", access_token); // Keep for compatibility

        // Update auth context - MUST set token BEFORE setUser to ensure SubscriptionContext has token
        setToken(access_token);
        setUser(business);

        console.log("[AuthCallback] Auth successful, subscription_status:", business.subscription_status);
        
        // Redirect based on subscription status
        // New users (inactive) go to complete-signup, active users go to dashboard
        const targetRoute = business.subscription_status === "inactive" 
          ? "/complete-signup" 
          : "/dashboard";
        
        navigate(targetRoute, { state: { user: business }, replace: true });

      } catch (error) {
        console.error("[AuthCallback] Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, setUser, setToken]);

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
